import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  Button, Dialog, DialogClose, EmptyState, ErrorState, Field, Input, Notice, Pagination, SearchInput, Select, SkeletonRows,
  Status, Surface, Textarea, Toolbar, cn, focusRing,
} from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { CADENCE, dayText } from "./payScheduleView";

const PAGE = 25;
const STATES = [
  ["current", "On it now"],
  ["upcoming", "Starting later"],
  ["ended", "Moved off or left"],
  ["all", "Everyone"],
];
const SOURCE = { hire: "Set when pay was set", hr: "Assigned by HR", transfer: "Moved here", migration: "Recorded at upgrade" };
const errorsOf = (error) => error?.response?.data?.errors || null;
const plain = (error, fallback) => String(error?.response?.data?.message || "").replace(/^Request failed( with status code)?:?\s*\d{3}\s*[—–-]?\s*/i, "").trim() || fallback;

/**
 * The preview the server makes of a bulk change (nothing is saved): every chosen employee is checked against the same
 * rules as a single assignment. Asked again whenever the choice changes, after a short pause.
 */
function usePreview(request) {
  const [state, setState] = useState({ loading: false, result: null, error: "" });
  const key = request ? JSON.stringify(request) : "";
  useEffect(() => {
    if (!key) { setState({ loading: false, result: null, error: "" }); return undefined; }
    let live = true;
    setState((current) => ({ ...current, loading: true, error: "" }));
    const timer = setTimeout(() => {
      businessApi.bulkPayAssignments({ ...JSON.parse(key), preview: true })
        .then((res) => { if (live) setState({ loading: false, result: res?.data || null, error: "" }); })
        .catch((error) => { if (live) setState({ loading: false, result: null, error: plain(error, "The check couldn't be done. Try again.") }); });
    }, 350);
    return () => { live = false; clearTimeout(timer); };
  }, [key]);
  return state;
}

function PreviewList({ preview }) {
  if (preview.loading) return <p className="text-body-sm text-tertiary" role="status">Checking each employee…</p>;
  if (preview.error) return <Notice tone="danger" title="Not checked">{preview.error}</Notice>;
  const result = preview.result;
  if (!result) return null;
  const blocked = result.results.filter((row) => !row.ok);
  return (
    <div className="space-y-2" role="status">
      <p className="text-body-sm text-foreground">
        <span className="font-medium">{result.eligible}</span> can be saved{blocked.length ? <>, <span className="font-medium">{blocked.length}</span> cannot</> : null}.
      </p>
      {blocked.length ? (
        <ul className="max-h-48 divide-y divide-subtle overflow-y-auto rounded-panel border border-default text-body-sm" aria-label="Employees who cannot be saved">
          {blocked.map((row) => (
            <li key={row.memberId} className="px-3 py-2">
              <span className="font-medium text-foreground">{row.name || "Employee"}</span>
              <span className="block text-meta text-secondary">{row.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {result.periodsToRecalculate?.length ? (
        <p className="text-meta text-secondary">Afterwards, recalculate {result.periodsToRecalculate.map((p) => p.name).join(", ")}: {result.periodsToRecalculate.length === 1 ? "it was" : "they were"} calculated before this change.</p>
      ) : null}
    </div>
  );
}

/** Saves only the employees the latest preview accepted; a race the preview could not see comes back as reasons. */
function useSubmit({ request, preview, onDone }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const eligible = (preview.result?.results || []).filter((row) => row.ok).map((row) => row.memberId);
  const submit = async () => {
    if (busy || !eligible.length) return;
    setBusy(true);
    setFailure(null);
    try {
      const res = await businessApi.bulkPayAssignments({ ...request, memberIds: eligible, preview: false });
      onDone(res?.data);
    } catch (error) {
      setFailure({ text: plain(error, "Nothing was saved. Try again."), result: errorsOf(error) });
      setBusy(false);
    }
  };
  return { busy, failure, eligible, submit };
}

/** Put employees who have pay but no pay schedule on this one (C2; the preview is the review step). */
function AddEmployeesDialog({ schedule, onClose, onDone }) {
  const [list, setList] = useState({ loading: true, rows: [] });
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState(() => new Set());
  const [start, setStart] = useState("first");
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    businessApi.getEmployeesWithoutPaySchedule()
      .then((res) => setList({ loading: false, rows: res?.data?.employees || [] }))
      .catch(() => setList({ loading: false, rows: [], error: true }));
  }, []);
  const matching = list.rows.filter((row) => !query || `${row.name} ${row.employeeCode || ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  const request = chosen.size && (start === "first" || date)
    ? { action: "assign", scheduleId: schedule.id, memberIds: [...chosen].sort((a, b) => a - b), ...(start === "first" ? { fromFirstPayDay: true } : { effectiveFrom: date }), reason: reason.trim() || null }
    : null;
  const preview = usePreview(request && { ...request, reason: null });
  const { busy, failure, eligible, submit } = useSubmit({ request, preview, onDone });
  const toggle = (id) => setChosen((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const allShown = matching.length > 0 && matching.every((row) => chosen.has(row.memberId));

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title={`Add employees to ${schedule.name}`}
      description="Employees who have pay but no pay schedule. Each is checked against the schedule's rules before anything is saved."
      footer={
        <>
          <DialogClose asChild><Button disabled={busy}>Cancel</Button></DialogClose>
          <Button variant="primary" loading={busy} disabled={!eligible.length || preview.loading} onClick={submit}>
            {eligible.length ? `Add ${eligible.length} employee${eligible.length === 1 ? "" : "s"}` : "Add employees"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {list.loading ? <SkeletonRows rows={3} columns={2} label="Loading employees" />
          : list.error ? <Notice tone="danger" title="Employees couldn't be loaded">Close this and try again.</Notice>
            : !list.rows.length ? <p className="text-body-sm text-secondary">Everyone with pay is on a pay schedule.</p>
              : (
                <>
                  <Toolbar>
                    <SearchInput label="Search employees" placeholder="Search by name or code" className="w-full sm:w-64" value={query} onChange={(event) => setQuery(event.target.value)} />
                    <Button size="sm" variant="quiet" onClick={() => setChosen((current) => {
                      const next = new Set(current);
                      matching.forEach((row) => (allShown ? next.delete(row.memberId) : next.add(row.memberId)));
                      return next;
                    })}>{allShown ? "Unselect shown" : `Select ${matching.length} shown`}</Button>
                    <p className="text-meta tabular-nums text-secondary" role="status">{chosen.size} selected of {list.rows.length}</p>
                  </Toolbar>
                  <fieldset>
                    <legend className="sr-only">Employees without a pay schedule</legend>
                    <ul className="max-h-64 divide-y divide-subtle overflow-y-auto rounded-panel border border-default">
                      {matching.length ? matching.map((row) => (
                        <li key={row.memberId}>
                          <label className="flex min-h-touch cursor-pointer items-center gap-3 px-3 py-2">
                            <input type="checkbox" checked={chosen.has(row.memberId)} disabled={busy} onChange={() => toggle(row.memberId)} className={focusRing} />
                            <span className="min-w-0">
                              <span className="block font-medium text-foreground">{row.name}</span>
                              <span className="block text-meta text-secondary">{row.employeeCode ? `${row.employeeCode} · ` : ""}pay without a schedule from {dayText(row.from)}</span>
                            </span>
                          </label>
                        </li>
                      )) : <li className="px-3 py-4 text-body-sm text-secondary">No employee matches “{query}”.</li>}
                    </ul>
                  </fieldset>
                  <fieldset className="space-y-2">
                    <legend className="text-label text-secondary">Starts</legend>
                    {[["first", "From each employee's first day of pay", "Every day they have pay is covered."], ["later", "From a later pay period", "Days before it stay without a pay schedule."]].map(([value, label, hint]) => (
                      <label key={value} className="flex min-h-touch cursor-pointer items-start gap-3 rounded-control border border-default px-3 py-2">
                        <input type="radio" name="bulk-start" value={value} checked={start === value} disabled={busy} onChange={() => setStart(value)} className={cn("mt-1", focusRing)} />
                        <span><span className="block font-medium text-foreground">{label}</span><span className="block text-meta text-secondary">{hint}</span></span>
                      </label>
                    ))}
                    {start === "later" ? (
                      <Field label="First day" required hint="The first day of a period of this schedule.">
                        <Input type="date" value={date} disabled={busy} onChange={(event) => setDate(event.target.value)} />
                      </Field>
                    ) : null}
                  </fieldset>
                  <Field label="Reason (optional)">
                    <Textarea rows={2} maxLength={500} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} />
                  </Field>
                  <PreviewList preview={preview} />
                  {failure ? <Notice tone="danger" title="Nothing was saved" role="alert">{failure.text}{failure.result ? <PreviewList preview={{ result: failure.result }} /> : null}</Notice> : null}
                </>
              )}
      </div>
    </Dialog>
  );
}

/**
 * Move the selected employees to another schedule (C2). A move happens only on a day this schedule's period ends and
 * the other's begins; the server lists those days and checks every employee before anything is saved.
 */
function MoveEmployeesDialog({ schedule, selected, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [dates, setDates] = useState(null);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const first = selected[0];
  useEffect(() => {
    businessApi.getPaySchedules({ status: "active" })
      .then((res) => setTargets((res?.data?.schedules || []).filter((row) => row.id !== schedule.id && row.frequency !== "monthly")))
      .catch(() => setTargets([]));
  }, [schedule.id]);
  const chooseTarget = async (value) => {
    setTargetId(value);
    setDate("");
    setDates(value ? "loading" : null);
    if (!value) return;
    try {
      const res = await businessApi.getPayScheduleTransferOptions(first.memberId, value);
      const list = res?.data?.dates || [];
      setDates({ list, reason: res?.data?.reason || "" });
      if (list.length) setDate(list[0]);
    } catch (error) {
      setDates({ list: [], reason: plain(error, "The dates couldn't be loaded.") });
    }
  };
  const request = targetId && date ? { action: "transfer", scheduleId: Number(targetId), memberIds: selected.map((row) => row.memberId).sort((a, b) => a - b), effectiveFrom: date, reason: reason.trim() || null } : null;
  const preview = usePreview(request && { ...request, reason: null });
  const { busy, failure, eligible, submit } = useSubmit({ request, preview, onDone });
  const loaded = dates && dates !== "loading" ? dates : null;
  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title={`Move ${selected.length} employee${selected.length === 1 ? "" : "s"} to another pay schedule`}
      description={`They move on a day a period of ${schedule.name} ends and a period of the new schedule begins, so no day is unpaid or paid twice.`}
      footer={
        <>
          <DialogClose asChild><Button disabled={busy}>Cancel</Button></DialogClose>
          <Button variant="primary" loading={busy} disabled={!eligible.length || preview.loading} onClick={submit}>
            {eligible.length ? `Move ${eligible.length} employee${eligible.length === 1 ? "" : "s"}` : "Move employees"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-body-sm text-secondary">{selected.slice(0, 4).map((row) => row.memberName).join(", ")}{selected.length > 4 ? ` and ${selected.length - 4} more` : ""}</p>
        <Field label="New pay schedule" required>
          <Select value={targetId} disabled={busy} onChange={(event) => chooseTarget(event.target.value)}>
            <option value="">Choose a pay schedule</option>
            {targets.map((row) => <option key={row.id} value={row.id}>{row.name} ({CADENCE[row.frequency]})</option>)}
          </Select>
        </Field>
        {dates === "loading" ? <p className="text-body-sm text-tertiary">Finding the days they can move…</p> : null}
        {loaded && loaded.list.length ? (
          <Field label="First day on the new schedule" required>
            <Select value={date} disabled={busy} onChange={(event) => setDate(event.target.value)}>
              {loaded.list.map((value) => <option key={value} value={value}>{dayText(value)}</option>)}
            </Select>
          </Field>
        ) : null}
        {loaded && !loaded.list.length ? <Notice tone="warning" title="No day works for these two schedules">{loaded.reason}</Notice> : null}
        {request ? (
          <Field label="Reason (optional)">
            <Textarea rows={2} maxLength={500} value={reason} disabled={busy} onChange={(event) => setReason(event.target.value)} />
          </Field>
        ) : null}
        <PreviewList preview={preview} />
        {failure ? <Notice tone="danger" title="Nothing was saved" role="alert">{failure.text}{failure.result ? <PreviewList preview={{ result: failure.result }} /> : null}</Notice> : null}
      </div>
    </Dialog>
  );
}

/**
 * Employees of one pay schedule (a large selectable collection, DESIGN.md › Data collections, class F): who is on it
 * now, who starts later, who moved off or left; search by name or code; pages of 25 in name order; state, search and
 * page in the URL. Selection survives paging and is cleared when the filter changes, so a count never hides anyone.
 */
export default function ScheduleEmployees({ schedule, canManage, onChanged, onNotice }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = STATES.some(([key]) => key === searchParams.get("state")) ? searchParams.get("state") : "current";
  const query = searchParams.get("q") || "";
  const page = Math.max(Number(searchParams.get("page")) || 1, 1);
  const [typed, setTyped] = useState(query);
  const [data, setData] = useState({ loading: true, error: false, rows: [], total: 0, counts: null });
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState(() => new Map());
  const [dialog, setDialog] = useState(null);
  const [unassigned, setUnassigned] = useState(0);

  useEffect(() => {
    if (typed.trim() === query) return undefined;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (typed.trim()) next.set("q", typed.trim()); else next.delete("q");
      next.delete("page");
      setSearchParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [typed, query, searchParams, setSearchParams]);

  useEffect(() => {
    let live = true;
    setData((current) => ({ ...current, loading: true, error: false }));
    businessApi.getPayScheduleEmployees(schedule.id, { state, search: query || undefined, page, limit: PAGE })
      .then((res) => { if (live) setData({ loading: false, error: false, rows: res?.data?.assignments || [], total: Number(res?.data?.total || 0), counts: res?.data?.counts || null }); })
      .catch(() => { if (live) setData((current) => ({ ...current, loading: false, error: true })); });
    return () => { live = false; };
  }, [schedule.id, state, query, page, attempt]);

  useEffect(() => { setSelected(new Map()); }, [state, query]);
  useEffect(() => {
    businessApi.getEmployeesWithoutPaySchedule().then((res) => setUnassigned((res?.data?.employees || []).length)).catch(() => setUnassigned(0));
  }, [attempt]);

  const pages = Math.max(Math.ceil(data.total / PAGE), 1);
  useEffect(() => {
    if (!data.loading && !data.error && page > pages) {
      const next = new URLSearchParams(searchParams);
      next.set("page", String(pages));
      setSearchParams(next, { replace: true });
    }
  }, [data.loading, data.error, page, pages, searchParams, setSearchParams]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };
  const movable = (row) => state !== "ended" && !row.effectiveUntil && row.effectiveFrom;
  const canMove = canManage && schedule.status === "active";
  const toggle = (row) => setSelected((current) => {
    const next = new Map(current);
    if (next.has(row.memberId)) next.delete(row.memberId); else next.set(row.memberId, row);
    return next;
  });
  const pageMovable = data.rows.filter(movable);
  const allOnPage = pageMovable.length > 0 && pageMovable.every((row) => selected.has(row.memberId));
  const selection = useMemo(() => [...selected.values()], [selected]);
  const finish = async (result, verb) => {
    setDialog(null);
    setSelected(new Map());
    setAttempt((n) => n + 1);
    await onChanged?.();
    const stale = result?.periodsToRecalculate || [];
    onNotice?.({ tone: "success", text: `${result?.eligible || 0} employee${result?.eligible === 1 ? "" : "s"} ${verb}.${stale.length ? ` Recalculate ${stale.map((p) => p.name).join(", ")}.` : ""}` });
  };

  const counts = data.counts || {};
  const toolbar = (
    <div className="space-y-3">
      <Toolbar
        label="Find employees"
        actions={canMove ? <Button onClick={() => setDialog("add")}>Add employees</Button> : null}
      >
        <SearchInput label="Search this schedule's employees" placeholder="Search by name or code" className="w-full sm:w-64" value={typed} onChange={(event) => setTyped(event.target.value)} />
        <Select aria-label="Show" className="w-auto" value={state} onChange={(event) => setParam("state", event.target.value === "current" ? "" : event.target.value)}>
          {STATES.map(([key, label]) => <option key={key} value={key}>{label}{counts[key] != null ? ` (${counts[key]})` : ""}</option>)}
        </Select>
        {query || state !== "current" ? <Button size="sm" variant="quiet" onClick={() => { setTyped(""); setSearchParams(new URLSearchParams()); }}>Clear</Button> : null}
      </Toolbar>
      {canMove && selected.size ? (
        <div className="flex flex-wrap items-center gap-2 rounded-control bg-accent-subtle px-3 py-2" role="status">
          <span className="text-body-sm font-medium text-foreground">{selected.size} selected</span>
          <Button size="sm" onClick={() => setDialog("move")}>Move to another schedule</Button>
          <Button size="sm" variant="quiet" onClick={() => setSelected(new Map())}>Clear selection</Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {unassigned && canMove ? (
        <Notice tone="warning" title={`${unassigned} employee${unassigned === 1 ? " has" : "s have"} pay but no pay schedule`} action={<Button size="sm" onClick={() => setDialog("add")}>Add employees</Button>}>
          No payroll pays them until they are on a schedule.
        </Notice>
      ) : null}
      <Surface toolbar={toolbar}>
        {data.error ? <ErrorState title="Employees couldn't be loaded" onRetry={() => setAttempt((n) => n + 1)} />
          : data.loading && !data.rows.length ? <div className="p-4"><SkeletonRows rows={4} columns={3} label="Loading employees" /></div>
            : !data.rows.length ? (
              query ? <EmptyState title="No employee matches" description={`Nobody here is named or coded like “${query}”.`} action={<Button size="sm" onClick={() => setTyped("")}>Clear search</Button>} />
                : <EmptyState title={state === "current" ? "Nobody is on this schedule now" : "Nobody here"} description={state === "current" && canMove ? "Add employees who have pay but no pay schedule, or move people here from another schedule's Employees." : undefined} />
            ) : (
              <>
                {/* The list's head row, in the table-head treatment: the page selection on the left, the count on the right. */}
                <div className="flex min-h-8 flex-wrap items-center justify-between gap-x-4 border-b border-subtle bg-surface-sunken px-4 text-cell-head text-secondary">
                  {canMove && pageMovable.length ? (
                    <label className="inline-flex cursor-pointer items-center gap-3 max-md:min-h-touch">
                      <input type="checkbox" checked={allOnPage} onChange={() => setSelected((current) => {
                        const next = new Map(current);
                        pageMovable.forEach((row) => (allOnPage ? next.delete(row.memberId) : next.set(row.memberId, row)));
                        return next;
                      })} className={focusRing} />
                      Select everyone on this page
                    </label>
                  ) : <span>Employee</span>}
                  <p className="tabular-nums" role="status">{data.total} employee{data.total === 1 ? "" : "s"}{query ? ` matching “${query}”` : ""}</p>
                </div>
                <ul className={cn("divide-y divide-subtle text-body-sm", data.loading && "opacity-60")} aria-label={`Employees of ${schedule.name}`}>
                  {data.rows.map((row) => (
                    <li key={row.id} data-selected={selected.has(row.memberId) || undefined} className={cn("flex items-center gap-3 px-4 py-2", selected.has(row.memberId) && "bg-accent-subtle")}>
                      {canMove ? (
                        // The box keeps its size; the label around it is the 44 px touch target on phones.
                        <label className="-my-2 -ml-4 flex shrink-0 cursor-pointer items-center self-stretch py-2 pl-4 max-md:-mr-3 max-md:min-h-touch max-md:w-11">
                          <input type="checkbox" aria-label={`Select ${row.memberName}`} disabled={!movable(row)} checked={selected.has(row.memberId)} onChange={() => toggle(row)} className={focusRing} />
                        </label>
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <Link to={`/business/hr/employees/${row.memberId}/pay`} className={cn("rounded-control font-medium text-foreground hover:underline", focusRing)}>{row.memberName}</Link>
                        <p className="text-meta text-secondary">
                          {row.employeeCode ? `${row.employeeCode} · ` : ""}
                          {row.effectiveUntil ? `${dayText(row.effectiveFrom)} – ${dayText(row.effectiveUntil)}` : `From ${dayText(row.effectiveFrom)}`}
                          {` · ${SOURCE[row.source] || row.source}`}
                        </p>
                      </div>
                      {row.memberStatus && row.memberStatus !== "active" ? <Status>{row.memberStatus === "terminated" ? "Left" : row.memberStatus}</Status> : null}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-subtle px-4"><Pagination page={Math.min(page, pages)} totalPages={pages} onPageChange={(value) => setParam("page", value > 1 ? String(value) : "")} label="Employee pages" /></div>
              </>
            )}
      </Surface>

      {dialog === "add" ? <AddEmployeesDialog schedule={schedule} onClose={() => setDialog(null)} onDone={(result) => finish(result, `added to ${schedule.name}`)} /> : null}
      {dialog === "move" ? <MoveEmployeesDialog schedule={schedule} selected={selection} onClose={() => setDialog(null)} onDone={(result) => finish(result, "moved")} /> : null}
    </div>
  );
}
